import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../../components/PersonaNavbar';
import Footer from '../../components/PersonaFooter';
import Button from '../../components/Button';
import SimplePersonaModal from '../../components/SimplePersonaModal';
import SimpleErrorModal from '../../components/SimpleErrorModal';

const apiUrlMultivac = process.env.REACT_APP_MULTIVAC_URL;

const filterOptions = [
  {
    label: "Industry",
    property: "industry",
    values: [
      "AI & MLOps",
      "Customer Support & Experience",
      "E-commerce & Retail",
      "Education & EdTech",
      "Energy & Sustainability",
      "Finance & FinTech",
      "Healthcare & Life Sciences",
      "HRTech & Workforce",
      "IT & Cloud Infrastructure",
      "LegalTech & Contract",
      "Logistics & Supply Chain",
      "Manufacturing & Industrial",
      "Marketing & AdTech",
      "Productivity & Collaboration",
      "Real Estate & PropTech",
      "Security & Compliance"
    ]
  },
  {
    label: "Company Size",
    property: "employee_range",
    values: ["10-100", "100-1000", "1000+"]
  },
  {
    label: "Department",
    property: "role_set_name",
    values: [
      "Accounting",
      "Administrative",
      "Arts and Design",
      "Business Development",
      "Community and Social Services",
      "Compliance",
      "Consulting",
      "Corporate Strategy",
      "Customer Success",
      "Customer Success and Support",
      "Data & Analytics",
      "Education",
      "Engineering",
      "Enterprise Architecture",
      "Enterprise Resource Planning (ERP)",
      "Entrepreneurship",
      "Executive Leadership",
      "Finance",
      "Healthcare Services",
      "Human Resources",
      "Information Technology",
      "Learning and Development",
      "Legal",
      "Marketing",
      "Media and Communication",
      "Operations",
      "Procurement",
      "Product Management",
      "Program and Project Management",
      "Purchasing",
      "Quality Assurance",
      "Research",
      "Research & Development (R&D)",
      "Risk Management",
      "Sales",
      "Security & Compliance",
      "Supply Chain",
      "Talent Development"
    ]
  },
];

const GeneratePersonas: React.FC = () => {
  const [selectedFilters, setSelectedFilters] = useState<{ property: string; values: string[]; }[]>([]);
  const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>({});
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [personaData, setPersonaData] = useState<any[]>([]);
  const [allPersonas, setAllPersonas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true); // Start with loading state true
  const [allFiltersSelected, setAllFiltersSelected] = useState(false);
  const [currentPersona, setCurrentPersona] = useState<any | null>(null);
  
  // Simple boolean state for modal visibility
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  
  // Fetch all personas when component mounts using search_persona with empty filters
  useEffect(() => {
    const fetchAllPersonas = async () => {
      try {
        const response = await fetch(`${apiUrlMultivac}/targetaudience/search_persona`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            search_fields: [] // Empty filters to get all personas
          }),
        });
        
        if (!response.ok) {
          console.error('Failed to fetch personas');
          return;
        }
        
        const result = await response.json();
        setAllPersonas(result);
      } catch (error) {
        console.error('Error fetching all personas:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllPersonas();
  }, []);
  
  // Filter available options based on current selections
  const filteredPersonasByCurrentSelection = useMemo(() => {
    if (selectedFilters.length === 0) return allPersonas;
    
    return allPersonas.filter(persona => {
      return selectedFilters.every(filter => {
        const propertyValue = persona[filter.property];
        return filter.values.includes(propertyValue);
      });
    });
  }, [allPersonas, selectedFilters]);
  
  // Get available options for each filter property based on current selections
  const availableOptions = useMemo(() => {
    const options: Record<string, Set<string>> = {};
    
    // Initialize with empty sets for each property
    filterOptions.forEach(option => {
      options[option.property] = new Set();
    });
    
    // Add available values from filtered personas
    filteredPersonasByCurrentSelection.forEach(persona => {
      filterOptions.forEach(option => {
        const value = persona[option.property];
        if (value) {
          options[option.property].add(value);
        }
      });
    });
    
    return options;
  }, [filteredPersonasByCurrentSelection]);
  
  const toggleDropdown = (property: string) => {
    setDropdownStates(prev => {
      // Close all dropdowns and only toggle the clicked one
      const newState = { [property]: !prev[property] };
      return newState;
    });
  };

  const handleFilterChange = (property: string, value: string) => {
    // Check if this value is already selected
    const isAlreadySelected = filterValues[property] === value;
    
    if (isAlreadySelected) {
      // If already selected, remove the filter
      setFilterValues(prev => {
        const newValues = { ...prev };
        delete newValues[property];
        return newValues;
      });
      
      setSelectedFilters(prev => prev.filter(f => f.property !== property));
    } else if (value) {
      // If not selected and value is provided, add/update the filter
      setFilterValues(prev => ({
        ...prev,
        [property]: value
      }));

      const newFilter = {
        property,
        values: [value]
      };
      
      setSelectedFilters(prev => [
        ...prev.filter(f => f.property !== property),
        newFilter
      ]);
    } else {
      // If value is empty, remove the filter
      setFilterValues(prev => {
        const newValues = { ...prev };
        delete newValues[property];
        return newValues;
      });
      
      setSelectedFilters(prev => prev.filter(f => f.property !== property));
    }

    setDropdownStates(prev => ({
      ...prev,
      [property]: false
    }));
  };

  useEffect(() => {
    // Check if all filters are selected
    const requiredProperties = filterOptions.map(option => option.property);
    const selectedProperties = selectedFilters.map(filter => filter.property);
    
    // Check if all required filters have been selected
    const allSelected = requiredProperties.every(prop => selectedProperties.includes(prop));
    setAllFiltersSelected(allSelected);
  }, [selectedFilters]);

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setFilterValues({});
  };
  
  const handleSearch = () => {
    // Reset modals first
    setIsPersonaModalOpen(false);
    setIsErrorModalOpen(false);
    
    setLoading(true);
    
    try {
      // Filter personas locally based on selected filters
      const filteredPersonas = allPersonas.filter(persona => {
        return selectedFilters.every(filter => {
          const propertyValue = persona[filter.property];
          return filter.values.includes(propertyValue);
        });
      });
      
      setPersonaData(filteredPersonas);
      
      // If we have results, show the first one
      if (filteredPersonas.length > 0) {
        setCurrentPersona(filteredPersonas[0]);
        setIsPersonaModalOpen(true);
        console.log("Opening persona modal with data", filteredPersonas[0]);
      } else {
        setIsErrorModalOpen(true);
        console.log("Opening error modal - no results found");
      }
    } catch (error) {
      console.error('Error filtering data:', error);
      setPersonaData([]);
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (!target.closest('.filter-dropdown')) {
        setDropdownStates({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClosePersonaModal = () => {
    console.log("Closing persona modal");
    setIsPersonaModalOpen(false);
  };

  const handleCloseErrorModal = () => {
    console.log("Closing error modal");
    setIsErrorModalOpen(false);
  };

  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#db2777] border-t-transparent mb-4"></div>
        <p className="text-[#db2777] font-medium">Loading...</p>
      </div>
    </div>
  );

  return (
    <div className='flex flex-col min-h-screen bg-gradient-to-b from-white via-gray-50 to-pink-50'>
      <Navbar />
      <main className='flex-grow'>
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold mb-3 text-center text-gray-800">Generate Your Ideal Personas</h1>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Create targeted buyer personas that match your specific market needs. Customize filters below to generate detailed personas that will help you understand your ideal customers better.
          </p>
          
          <div className="bg-white p-8 rounded-xl shadow-lg mb-10 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mr-3 text-[#db2777]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Choose Attributes to Generate Personas
            </h2>
            <p className="text-gray-600 mb-6 ml-10">
              Select from the options below to narrow down your persona search. All filters are required to generate detailed personas.
            </p>
            <div className="mb-8">
              <div className="w-full">
                <div className="bg-white rounded-lg">
                  <div className="grid grid-cols-1 gap-6">
                    {filterOptions.map((option) => (
                      <div key={option.property} className="relative filter-dropdown">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {option.label} <span className="text-red-500">*</span>
                        </label>
                        <button
                          onClick={() => toggleDropdown(option.property)}
                          className={`w-full px-4 py-3 text-left bg-white border rounded-lg transition-all duration-200 hover:border-[#db2777] ${
                            dropdownStates[option.property] 
                              ? 'border-[#db2777] ring-2 ring-pink-100' 
                              : 'border-gray-200'
                          } hover:shadow-md`}
                        >
                          <div className="flex justify-between items-center">
                            <span className={filterValues[option.property] ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                              {filterValues[option.property] || `Select ${option.label}`}
                            </span>
                            <svg className={`h-5 w-5 transition-transform duration-200 ${dropdownStates[option.property] ? 'transform rotate-180 text-[#db2777]' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </button>
                        {dropdownStates[option.property] && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {option.values
                              .filter(value => {
                                // If no selection has been made yet, show all options
                                if (selectedFilters.length === 0) return true;
                                
                                // For the currently open dropdown, show all options that are compatible with other selected filters
                                if (dropdownStates[option.property]) {
                                  // Create a temporary set of filters excluding the current property
                                  const otherFilters = selectedFilters.filter(f => f.property !== option.property);
                                  
                                  // If no other filters are selected, show all options
                                  if (otherFilters.length === 0) return true;
                                  
                                  // Check if this value would have any matching personas with the other filters
                                  return allPersonas.some(persona => {
                                    // Check if this persona matches this property's value
                                    if (persona[option.property] !== value) return false;
                                    
                                    // Check if this persona matches all other selected filters
                                    return otherFilters.every(filter => {
                                      const propertyValue = persona[filter.property];
                                      return filter.values.includes(propertyValue);
                                    });
                                  });
                                }
                                
                                // If this property is already selected, include its selected value
                                const existingFilter = selectedFilters.find(f => f.property === option.property);
                                if (existingFilter && existingFilter.values.includes(value)) return true;
                                
                                // Otherwise, only show values that exist in the filtered personas
                                return availableOptions[option.property].has(value);
                              })
                              .map((value) => (
                                <button
                                  key={value}
                                  className={`w-full px-4 py-2 text-left hover:bg-pink-50 transition-colors duration-150 ${
                                    filterValues[option.property] === value ? 'bg-pink-50 text-[#db2777] font-medium' : ''
                                  }`}
                                  onClick={() => handleFilterChange(option.property, value)}
                                >
                                  {value}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Selected Filters Display */}
                  {selectedFilters.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-full mb-2 text-sm font-medium text-gray-700">Active filters:</div>
                      {selectedFilters.map((filter, index) => {
                        const filterOption = filterOptions.find(opt => opt.property === filter.property);
                        return (
                          <div
                            key={index}
                            className="flex items-center bg-pink-100 text-[#db2777] px-3 py-2 rounded-full transition-all hover:bg-pink-200 shadow-sm"
                          >
                            <span className="text-sm font-medium">{filterOption?.label}: {filter.values[0]}</span>
                            <button
                              onClick={() => handleFilterChange(filter.property, '')}
                              className="ml-2 hover:bg-pink-200 rounded-full p-1"
                              aria-label="Remove filter"
                            >
                              <svg
                                className="h-3 w-3"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                      
                      <button 
                        onClick={clearAllFilters}
                        className="flex items-center bg-gray-100 text-gray-600 px-3 py-2 rounded-full hover:bg-gray-200 transition-colors text-sm font-medium shadow-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear All
                      </button>
                    </div>
                  )}

                  <div className="mt-8 flex justify-center">
                    <Button 
                      primary 
                      onClick={handleSearch}
                      disabled={!allFiltersSelected}
                      className="px-8 py-3 text-base font-medium shadow-md hover:shadow-xl transition-all duration-200 w-full md:w-auto max-w-xs disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Generate Personas
                      </div>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      {loading && <LoadingOverlay />}
      
      {/* Simple Error Modal with clear boolean state */}
      <SimpleErrorModal
        isOpen={isErrorModalOpen}
        onClose={handleCloseErrorModal}
        message="We couldn't find any personas matching your selected criteria. Please try a different combination of filters."
      />
      
      {/* Simple Persona Modal with clear boolean state */}
      {currentPersona && (
        <SimplePersonaModal
          isOpen={isPersonaModalOpen}
          onClose={handleClosePersonaModal}
          persona={currentPersona}
        />
      )}
    </div>
  );
};

export default GeneratePersonas;
